import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Menu } from 'semantic-ui-react';
import { Popup } from '../../lib/custom-ui';
import pick from 'lodash/pick';

import { useSteps } from '../../hooks';
import actions from '../../actions';
import selectors from '../../selectors';
import DeleteStep from '../DeleteStep';
import ArchiveStep from '../ArchiveStep';
import ConfirmStep from '../ConfirmStep';
import ListMoveStep from '../ListMoveStep';
import ListCloneStep from '../ListCloneStep';

import styles from './ActionsStep.module.scss';

const StepTypes = {
  DELETE: 'DELETE',
  ARCHIVE: 'ARCHIVE',
  MOVE: 'MOVE',
  MOVE_CONFIRM: 'MOVE_CONFIRM',
  MOVE_CHECKING: 'MOVE_CHECKING',
  CLONE: 'CLONE',
  CLONE_CHECKING: 'CLONE_CHECKING',
};

const ActionsStep = React.memo(
  ({
    list,
    projectsToLists,
    onNameEdit,
    onCardAdd,
    onMove,
    onTransfer,
    onPrepareTransfer,
    onClone,
    onMissingAssigneesTransfer,
    onMissingAssigneesClone,
    onDelete,
    onArchive,
    canEdit,
    isSuperuser,
    isMonthlyReportAvailable,
    onMonthlyReportOpen,
    onClose,
  }) => {
    const [t] = useTranslation();
    const dispatch = useDispatch();
    const [step, openStep, handleBack] = useSteps();
    const listTransferPrepare = useSelector(selectors.selectListTransferPrepare);
    const pendingCloneRef = useRef(null);

    const handleEditNameClick = useCallback(() => {
      onNameEdit();
      onClose();
    }, [onNameEdit, onClose]);

    const handleAddCardClick = useCallback(() => {
      onCardAdd();
      onClose();
    }, [onCardAdd, onClose]);

    const handleDeleteClick = useCallback(() => {
      openStep(StepTypes.DELETE);
    }, [openStep]);

    const handleArchiveClick = useCallback(() => {
      openStep(StepTypes.ARCHIVE);
    }, [openStep]);

    const handleMoveClick = useCallback(() => {
      openStep(StepTypes.MOVE);
    }, [openStep]);

    const handleCloneClick = useCallback(() => {
      openStep(StepTypes.CLONE);
    }, [openStep]);

    const handleMoveDestinationSelect = useCallback(
      (boardId) => {
        if (String(boardId) === String(list.boardId)) {
          onClose();
          return;
        }

        openStep(StepTypes.MOVE_CHECKING, { boardId });
        onPrepareTransfer(boardId);
      },
      [list.boardId, onClose, openStep, onPrepareTransfer],
    );

    const handleCloneSubmit = useCallback(
      (boardId, navigateAfterClone) => {
        pendingCloneRef.current = { boardId, navigateAfterClone };
        openStep(StepTypes.CLONE_CHECKING);
        onPrepareTransfer(boardId);
      },
      [openStep, onPrepareTransfer],
    );

    useEffect(() => {
      if (!listTransferPrepare || String(listTransferPrepare.listId) !== String(list.id)) {
        return;
      }

      if (listTransferPrepare.status === 'pending') {
        return;
      }

      if (listTransferPrepare.status === 'failure') {
        if (pendingCloneRef.current) {
          openStep(StepTypes.CLONE);
          pendingCloneRef.current = null;
        } else {
          openStep(StepTypes.MOVE);
        }
        dispatch(actions.clearPrepareTransferList());
        return;
      }

      if (listTransferPrepare.status === 'success') {
        const { boardId, users, pointSystem } = listTransferPrepare;
        dispatch(actions.clearPrepareTransferList());

        if (pendingCloneRef.current) {
          const { navigateAfterClone } = pendingCloneRef.current;
          pendingCloneRef.current = null;

          if (users && users.length > 0) {
            onMissingAssigneesClone(boardId, users, navigateAfterClone);
            onClose();
            return;
          }

          onClone(boardId, navigateAfterClone);
          onClose();
          return;
        }

        if (users && users.length > 0) {
          onMissingAssigneesTransfer(boardId, users, pointSystem);
          onClose();
          return;
        }

        if (pointSystem && pointSystem.actionHint === 'enable_and_apply') {
          dispatch(
            actions.promptListTransferPointSystem({
              mode: 'enable_and_apply',
              listId: list.id,
              boardId,
              index: 0,
              addUserIds: [],
              templateId: pointSystem.sourceTemplate && pointSystem.sourceTemplate.id,
              templateName: (pointSystem.sourceTemplate && pointSystem.sourceTemplate.name) || '',
            }),
          );
          onClose();
          return;
        }

        openStep(StepTypes.MOVE_CONFIRM, { boardId });
      }
    }, [
      listTransferPrepare,
      list.id,
      dispatch,
      openStep,
      onMissingAssigneesTransfer,
      onMissingAssigneesClone,
      onClone,
      onClose,
    ]);

    const handleMoveConfirm = useCallback(() => {
      if (!step || !step.params) {
        return;
      }

      const { boardId } = step.params;

      if (String(boardId) !== String(list.boardId)) {
        onTransfer(boardId);
      }

      onClose();
    }, [step, list.boardId, onTransfer, onClose]);

    const handleMonthlyReportClick = useCallback(() => {
      onMonthlyReportOpen();
      onClose();
    }, [onMonthlyReportOpen, onClose]);

    if (step && step.type === StepTypes.DELETE) {
      return (
        <DeleteStep
          title="common.deleteList"
          content="common.areYouSureYouWantToDeleteThisList"
          buttonContent="action.deleteList"
          onConfirm={onDelete}
          onBack={handleBack}
        />
      );
    }

    if (step && step.type === StepTypes.ARCHIVE) {
      return (
        <ArchiveStep
          title="Архивировать"
          content="Поместить список в архив?"
          buttonContent="Архивировать"
          onConfirm={onArchive}
          onBack={handleBack}
        />
      );
    }

    if (step && step.type === StepTypes.MOVE) {
      return (
        <ListMoveStep
          projectsToLists={projectsToLists}
          defaultPath={pick(list, ['projectId', 'boardId'])}
          onMove={onMove}
          onTransfer={handleMoveDestinationSelect}
          onBack={handleBack}
          onClose={onClose}
        />
      );
    }

    if (step && step.type === StepTypes.CLONE) {
      return (
        <ListCloneStep
          projectsToLists={projectsToLists}
          defaultPath={pick(list, ['projectId', 'boardId'])}
          onClone={handleCloneSubmit}
          onBack={handleBack}
          onClose={onClose}
          isSubmitting={false}
        />
      );
    }

    if (step && step.type === StepTypes.MOVE_CHECKING) {
      return (
        <>
          <Popup.Header onBack={handleBack}>
            {t('common.moveList', {
              context: 'title',
            })}
          </Popup.Header>
          <Popup.Content>
            <div className={styles.content}>
              {t('common.loading', { defaultValue: 'Загрузка…' })}
            </div>
          </Popup.Content>
        </>
      );
    }

    if (step && step.type === StepTypes.CLONE_CHECKING) {
      return (
        <>
          <Popup.Header onBack={handleBack}>
            {t('common.cloneList', {
              context: 'title',
              defaultValue: 'Клонирование списка',
            })}
          </Popup.Header>
          <Popup.Content>
            <div className={styles.content}>
              {t('common.loading', { defaultValue: 'Загрузка…' })}
            </div>
          </Popup.Content>
        </>
      );
    }

    if (step && step.type === StepTypes.MOVE_CONFIRM) {
      return (
        <ConfirmStep
          title="common.moveList"
          content="common.areYouSureYouWantToMoveThisList"
          buttonContent="action.move"
          onConfirm={handleMoveConfirm}
          onBack={handleBack}
        />
      );
    }

    return (
      <>
        <Popup.Header>
          {t('common.listActions', {
            context: 'title',
          })}
        </Popup.Header>
        <Popup.Content>
          <Menu secondary vertical className={styles.menu}>
            {canEdit && (
              <Menu.Item className={styles.menuItem} onClick={handleEditNameClick}>
                {t('action.editTitle', {
                  context: 'title',
                })}
              </Menu.Item>
            )}
            {canEdit && (
              <Menu.Item className={styles.menuItem} onClick={handleAddCardClick}>
                {t('action.addCard', {
                  context: 'title',
                })}
              </Menu.Item>
            )}

            {isMonthlyReportAvailable && (
              <Menu.Item className={styles.menuItem} onClick={handleMonthlyReportClick}>
                {t('Отчет за месяц')}
              </Menu.Item>
            )}

            {canEdit && (
              <Menu.Item className={styles.menuItem} onClick={handleMoveClick}>
                {t('action.move')}
              </Menu.Item>
            )}

            {canEdit && (
              <Menu.Item className={styles.menuItem} onClick={handleCloneClick}>
                {t('action.clone', { defaultValue: 'Клонировать' })}
              </Menu.Item>
            )}

            {canEdit && (
              <Menu.Item className={styles.menuItem} onClick={handleArchiveClick}>
                {t('В архив', {
                  context: 'title',
                })}
              </Menu.Item>
            )}
          </Menu>
        </Popup.Content>
      </>
    );
  },
);

ActionsStep.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  list: PropTypes.object.isRequired,
  projectsToLists: PropTypes.array.isRequired,
  /* eslint-enable react/forbid-prop-types */
  onNameEdit: PropTypes.func.isRequired,
  onCardAdd: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onTransfer: PropTypes.func.isRequired,
  onPrepareTransfer: PropTypes.func.isRequired,
  onClone: PropTypes.func.isRequired,
  onMissingAssigneesTransfer: PropTypes.func.isRequired,
  onMissingAssigneesClone: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  canEdit: PropTypes.bool.isRequired,
  isSuperuser: PropTypes.bool,
  isMonthlyReportAvailable: PropTypes.bool.isRequired,
  onMonthlyReportOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ActionsStep;
